import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return ((data as { data?: unknown })?.data ?? data) as T;
}

export interface ConsignmentReceiptLine {
  id?: string;
  line_number?: number;
  item_id: string;
  description?: string | null;
  qty: number;
  uom?: string;
  heat_number?: string | null;
  dimension?: string | null;
  serial_number?: string | null;
  batch_number?: string | null;
  location_id?: string | null;
  expiry_date?: string | null;
  remarks?: string | null;
}

export interface ConsignmentReceipt {
  id: string;
  company_id: string;
  receipt_number: string;
  supplier_id: string;
  warehouse_id: string;
  receipt_date: string;
  status: string;
  remarks?: string | null;
  posted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  lines?: ConsignmentReceiptLine[];
}

export interface ConsignmentReceiptInput {
  supplier_id: string;
  warehouse_id: string;
  receipt_date: string;
  remarks?: string | null;
  lines: ConsignmentReceiptLine[];
}

export interface ConsignmentReturnLine {
  id?: string;
  line_number?: number;
  item_id: string;
  qty: number;
  uom?: string;
  heat_number?: string | null;
  dimension?: string | null;
  serial_number?: string | null;
  location_id?: string | null;
  remarks?: string | null;
}

export interface ConsignmentReturn {
  id: string;
  company_id: string;
  return_number: string;
  supplier_id: string;
  warehouse_id: string;
  return_date: string;
  status: string;
  remarks?: string | null;
  posted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  lines?: ConsignmentReturnLine[];
}

export interface ConsignmentReturnInput {
  supplier_id: string;
  warehouse_id: string;
  return_date: string;
  remarks?: string | null;
  lines: ConsignmentReturnLine[];
}

export type ConsignmentListFilters = {
  status?: string;
  supplier_id?: string;
  warehouse_id?: string;
};

class ConsignmentService {
  async listReceipts(params?: ConsignmentListFilters): Promise<ConsignmentReceipt[]> {
    const res = await api.getClient().get('/inventory/consignment-receipts', { params });
    const data = unwrap<ConsignmentReceipt[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async getReceipt(id: string): Promise<ConsignmentReceipt> {
    const res = await api.getClient().get(`/inventory/consignment-receipts/${id}`);
    return unwrap<ConsignmentReceipt>(res);
  }

  async createReceipt(body: ConsignmentReceiptInput): Promise<ConsignmentReceipt> {
    const res = await api.getClient().post('/inventory/consignment-receipts', body);
    return unwrap<ConsignmentReceipt>(res);
  }

  async postReceipt(id: string): Promise<ConsignmentReceipt> {
    const res = await api.getClient().post(`/inventory/consignment-receipts/${id}/post`);
    return unwrap<ConsignmentReceipt>(res);
  }

  async listReturns(params?: ConsignmentListFilters): Promise<ConsignmentReturn[]> {
    const res = await api.getClient().get('/inventory/consignment-returns', { params });
    const data = unwrap<ConsignmentReturn[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async getReturn(id: string): Promise<ConsignmentReturn> {
    const res = await api.getClient().get(`/inventory/consignment-returns/${id}`);
    return unwrap<ConsignmentReturn>(res);
  }

  async createReturn(body: ConsignmentReturnInput): Promise<ConsignmentReturn> {
    const res = await api.getClient().post('/inventory/consignment-returns', body);
    return unwrap<ConsignmentReturn>(res);
  }

  async postReturn(id: string): Promise<ConsignmentReturn> {
    const res = await api.getClient().post(`/inventory/consignment-returns/${id}/post`);
    return unwrap<ConsignmentReturn>(res);
  }
}

export default new ConsignmentService();
